import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'

const DateRangeSelector = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [customOpen, setCustomOpen] = useState(false)
  const [customStart, setCustomStart] = useState(moment(value.startDate))
  const [customEnd, setCustomEnd] = useState(moment(value.endDate))

  const presets = [
    {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().endOf('day'),
    },
    {
      label: 'This Week',
      start: moment().startOf('week'),
      end: moment().endOf('week'),
    },
    {
      label: 'This Month',
      start: moment().startOf('month'),
      end: moment().endOf('month'),
    },
    {
      label: 'Last Month',
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    },
  ]

  const label = `${moment(value.startDate).format('D MMM YYYY')} – ${moment(
    value.endDate,
  ).format('D MMM YYYY')}`

  const applyCustomRange = () => {
    if (!customStart?.isValid() || !customEnd?.isValid()) return
    if (customEnd.isBefore(customStart)) return

    onChange({
      startDate: customStart.clone().startOf('day'),
      endDate: customEnd.clone().endOf('day'),
    })

    setCustomOpen(false)
  }

  return (
    <>
      <TextField
        fullWidth
        value={label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        InputProps={{
          readOnly: true,
          endAdornment: <KeyboardArrowDown />,
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiInputBase-input': {
            cursor: 'pointer',
            // fontWeight: 700,
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {presets.map((preset) => (
          <MenuItem
            key={preset.label}
            onClick={() => {
              onChange({
                startDate: preset.start.clone(),
                endDate: preset.end.clone(),
              })
              setAnchorEl(null)
            }}
          >
            {preset.label}
          </MenuItem>
        ))}

        <MenuItem
          onClick={() => {
            setCustomStart(moment(value.startDate))
            setCustomEnd(moment(value.endDate))
            setAnchorEl(null)
            setCustomOpen(true)
          }}
        >
          <Typography color="primary">Custom Range...</Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Custom date range</DialogTitle>

        <DialogContent>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale="en-gb"
          >
            <Stack gap={2} mt={1}>
              <DatePicker
                label="Start date"
                value={customStart}
                onChange={(newValue) => setCustomStart(newValue)}
                slotProps={{
                  textField: { fullWidth: true },
                }}
              />

              <DatePicker
                label="End date"
                value={customEnd}
                minDate={customStart}
                onChange={(newValue) => setCustomEnd(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      customStart?.isValid() &&
                      customEnd?.isValid() &&
                      customEnd.isBefore(customStart),
                    helperText:
                      customStart?.isValid() &&
                      customEnd?.isValid() &&
                      customEnd.isBefore(customStart)
                        ? 'End date must be after start date'
                        : '',
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyCustomRange}
            disabled={
              !customStart?.isValid() ||
              !customEnd?.isValid() ||
              customEnd.isBefore(customStart)
            }
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DateRangeSelector
